import { useEffect, FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { Header } from 'dibs-elements/exports/Header';
import { Button } from 'dibs-elements/exports/Button';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { trackEvent } from 'dibs-tracking';
import { useViewMore } from '../hooks/viewMore/useViewMore';
import { SbSharedRelatedSeller } from './SbSharedRelatedSeller';
import { relatedSellersTitle, viewMore, viewLess } from '../sbMessages';
import { INITIAL_VISIBLE_RELATED_CREATORS } from '../../constants/sbConstants';
import { Visibility } from '../../components/global/Visibility';
import styles from './SbSharedRelatedSellers.scss';
import classnames from 'classnames';

import type { SbSharedRelatedSellers_creatorRootPage$key } from './__generated__/SbSharedRelatedSellers_creatorRootPage.graphql';
import type { SbSharedRelatedSellers_itemSearch$key } from './__generated__/SbSharedRelatedSellers_itemSearch.graphql';
import type { SbSharedRelatedSellers_user$key } from './__generated__/SbSharedRelatedSellers_user.graphql';
import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers';
import { useSbSelector } from '../../reducers/useSbSelector';
import { trackSellerBrandingRemovalAbTestVariant } from 'dibs-buyer-layout/exports/sellerBrandingRemovalAbTestHelpers';

type Props = {
    creatorRootPage: SbSharedRelatedSellers_creatorRootPage$key;
    itemSearch: SbSharedRelatedSellers_itemSearch$key;
    user: SbSharedRelatedSellers_user$key;
    revealCreatorCount: number;
    classname?: string;
    isMobile?: boolean;
};

export const SbSharedRelatedSellers: FC<Props> = ({
    creatorRootPage: creatorRootPageRef,
    itemSearch: itemSearchRef,
    user: userRef,
    revealCreatorCount,
    classname,
    isMobile,
}) => {
    const creatorRootPage = useFragment(
        graphql`
            fragment SbSharedRelatedSellers_creatorRootPage on CreatorRootPage {
                verticals
                relatedSellers {
                    seller {
                        ...SbSharedRelatedSeller_seller
                        ...sellerBrandingHelpers_seller
                    }
                    storefrontItems {
                        ...SbSharedRelatedSeller_sellerStorefrontItems
                    }
                    creatorShopLink {
                        ...SbSharedRelatedSeller_creatorShopLink
                    }
                }
            }
        `,
        creatorRootPageRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRelatedSellers_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRelatedSeller_itemSearch
                meta {
                    header
                }
            }
        `,
        itemSearchRef
    );
    const user = useFragment(
        graphql`
            fragment SbSharedRelatedSellers_user on User {
                ...SbSharedRelatedSeller_user
                ...sellerBrandingHelpers_user
            }
        `,
        userRef
    );

    const creatorName = itemSearch?.meta?.header;
    const topCategoryL1 = creatorRootPage?.verticals?.[0];
    const relatedSellers = creatorRootPage?.relatedSellers;
    const relatedSellersStorefrontItems = relatedSellers?.storefrontItems || [];
    const relatedSellersCount = relatedSellersStorefrontItems.length;

    const isClient = useSbSelector(state => state.relayVariables.variables.isClient);
    const isSellerRewardsTier = (relatedSellers?.seller || []).every(seller =>
        checkRewardedSeller(seller || null)
    );
    const isUnderTopLevelLoyaltyTiers = checkTradeUserUnderTopLevelLoyaltyTiers(user || null);
    const trackSellerBrandingRemoval =
        isClient && !isSellerRewardsTier && !isUnderTopLevelLoyaltyTiers;

    useEffect(() => {
        if (trackSellerBrandingRemoval) {
            trackSellerBrandingRemovalAbTestVariant();
        }
    }, [trackSellerBrandingRemoval]);

    const { viewMoreEl, dispatch, visibleCount } = useViewMore<HTMLButtonElement>({
        totalCount: relatedSellersCount,
        revealCount: revealCreatorCount,
        initialCount: INITIAL_VISIBLE_RELATED_CREATORS,
    });

    if (topCategoryL1 && creatorName && relatedSellersCount >= INITIAL_VISIBLE_RELATED_CREATORS) {
        return (
            <HeadingLevel>
                {Heading => (
                    <div className={classnames(styles.relatedCreatorsWrapper, classname)}>
                        <div className={styles.relatedCreatorsHeader}>
                            <Header
                                title={relatedSellersTitle(topCategoryL1, creatorName)}
                                htmlElementType={Heading}
                            />
                        </div>
                        <Visibility
                            onContentVisible={() =>
                                trackEvent({
                                    category: 'promo interaction',
                                    action: 'related seller module displayed',
                                    label: 'none',
                                })
                            }
                        />
                        <div className={styles.relatedCreators}>
                            {relatedSellersStorefrontItems.map((storefrontItems, index) => {
                                const shouldHideRelatedCreator = index + 1 > visibleCount;
                                const seller = relatedSellers?.seller?.[index];
                                const creatorShopLink = relatedSellers?.creatorShopLink?.[index];

                                if (storefrontItems && seller && creatorShopLink) {
                                    return (
                                        <SbSharedRelatedSeller
                                            key={`related-seller-${index}`}
                                            sellerStorefrontItems={storefrontItems}
                                            seller={seller}
                                            creatorShopLink={creatorShopLink}
                                            isHidden={shouldHideRelatedCreator}
                                            itemSearch={itemSearch}
                                            user={user}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                        {relatedSellersCount > INITIAL_VISIBLE_RELATED_CREATORS && (
                            <Button
                                fullWidth={isMobile}
                                buttonRef={viewMoreEl}
                                type="secondary"
                                size="medium"
                                dataTn="related-sellers-view-more"
                                onClick={e => {
                                    e.currentTarget.blur();
                                    dispatch();
                                }}
                                className={styles.viewMoreButton}
                            >
                                {visibleCount >= relatedSellersCount ? viewLess : viewMore}
                            </Button>
                        )}
                    </div>
                )}
            </HeadingLevel>
        );
    } else {
        return null;
    }
};
